
/* eslint-disable vue/no-mutating-props */
// `target` is the checkup page's shared form-model object (mixins/checkup-page.js
// pattern, matching components/schedule/v2/steps/*), passed by reference so this
// step can push/splice its hospitals list and flip no_hospitals in place by design.
import GuidanceCard from '../parts/GuidanceCard.vue'
import SelectedItem from '../parts/SelectedItem.vue'
import SearchScopeRow from '../parts/SearchScopeRow.vue'
import searchScope from '~/mixins/checkup-search-scope'

export default {
    components: { GuidanceCard, SelectedItem, SearchScopeRow },
    mixins: [searchScope],
    // 100 miles, not the 25 the other geographic steps default to (#656
    // comment 4 item 5): hospitals are destination care — a client in
    // Swansea uses Boston hospitals ~50 miles away, and at 25 miles "mass
    // general"/"brigham"/"lahey" all came back empty. The scope row still
    // lets the client narrow it.
    searchDistance: 100,
    props: {
        target: { type: Object, required: true },
        // Same contract as DoctorsStep: pass empty strings on the merged
        // doctors+hospitals page, which owns the page heading (#656).
        heading: { type: String, default: 'Which hospitals do you prefer?' },
        lede: { type: String, default: 'We\'ll check that plans include the hospitals you\'d want to use. Optional: add them below, or let us know you don\'t have a preference.' },
    },
    data () {
        return {
            results: [],
            searching: false,
            // The last search failed (fetchHospitals gave null), as opposed to
            // finding nothing (#726).
            unavailable: false,
            // Monotonic guard against out-of-order search responses — same
            // rationale as DoctorsStep.searchSeq.
            searchSeq: 0,
            manual: { name: '' },
        }
    },
    methods: {
        async onSearch (query) {
            const seq = ++this.searchSeq
            this.query = query
            if (!query) { this.results = []; this.searching = false; this.unavailable = false; return }
            this.searching = true
            // zip and distance are the client's own picks, editable in the
            // SearchScopeRow above (mixins/checkup-search-scope.js) — not the
            // read-only target.zip_code snapshot they were.
            //
            // Oracle matches each word as the start of a name word, so "mass
            // general" finds Massachusetts General in one call (#726; it used
            // to need a per-word retry, #656 comment 4 item 5).
            const results = await this.fetchHospitals(query)
            if (seq !== this.searchSeq) { return } // stale — a newer search owns the state
            this.searching = false
            this.unavailable = results === null
            this.results = results || []
        },
        // One raw hospital_search call: the results array, or null on error
        // (an outage, which is not "no matches"). Oracle's hospital_search
        // returns a flat result (confirmed against the Oracle OpenAPI spec) —
        // no plural/singular location split like provider_search.
        async fetchHospitals (query) {
            const resp = await this.$api.searchHospitals(
                { zip_code: this.zip, query, distance: this.distance, page: 0, page_size: 10 },
                { spinner: false },
            ).catch(() => null)
            return resp ? (resp.results || []) : null
        },
        isAdded (r) {
            return (this.target.hospitals || []).some(h => (r.provider_id ? h.provider_id === r.provider_id : h.name === r.name))
        },
        add (r) {
            // Whitelisted to match the Hospital serializer exactly (flow/consults/
            // serializers.py:90-100) — raw search results carry extra Oracle
            // fields (ratings, ownership, latitude/longitude, address2,
            // zip_code_ext, has_emergency_services, meets_birthing_friendly)
            // that must never be persisted onto the survey.
            // Reassign rather than push() — target.hospitals is proxied
            // through to the page's reactive this.form.hospitals, and Vue's
            // deep watcher in mixins/survey-form.js hands the SAME array
            // reference as both newValue/oldValue on an in-place mutation, so
            // its `_.isEqual(newValue, oldValue)` guard would silently
            // swallow the change and nothing would autosave.
            this.target.hospitals = [
                ...this.target.hospitals,
                {
                    name: r.name,
                    provider_id: r.provider_id,
                    address: r.address,
                    city: r.city,
                    state: r.state,
                    zip_code: r.zip_code,
                    phone: r.phone,
                    hospital_type: r.hospital_type,
                    overall_rating: r.overall_rating,
                    distance: r.distance,
                },
            ]
            this.setOptOut(false)
            this.results = []
            this.$refs.card.clearSearch()
        },
        addManual (close) {
            if (!this.manual.name.trim()) { return }
            this.target.hospitals = [...this.target.hospitals, { name: this.manual.name.trim() }]
            this.manual = { name: '' }
            this.setOptOut(false)
            close()
        },
        remove (i) { this.target.hospitals = this.target.hospitals.filter((_, idx) => idx !== i) },
        setOptOut (v) { this.$set(this.target, 'no_hospitals', v) },
        onClearAndOptOut () {
            this.target.hospitals = []
            this.setOptOut(true)
        },
        resultMeta (r) {
            // Matches the MedicareCheckup platform's DcHospitalSearch subtitle
            // ('City, ST — Hospital Type') rather than flow's old address-led
            // format — the address is redundant with a name+city match, and
            // this reads more like a scannable dropdown row. Distance is
            // still appended (rounded to 1 decimal for readability — the raw
            // Oracle value is stored unchanged on add(), target.hospitals[].distance).
            const place = [r.city, r.state].filter(Boolean).join(', ')
            const withType = [place, r.hospital_type].filter(Boolean).join(' — ')
            return r.distance != null ? `${withType} · ${Number(r.distance).toFixed(1)} mi` : withType
        },
        hospitalSub (h) {
            if (!h.hospital_type) { return null }
            return `${h.hospital_type}${h.overall_rating ? ` · ${h.overall_rating}★ CMS rating` : ''}`
        },
        addrFor (h) {
            return h.address ? [h.address, `${h.city}, ${h.state} ${h.zip_code}`].filter(Boolean).join(', ') : 'Added manually'
        },
    },
}
